/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 22/12/21
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxios } from '@/components/AxiosContext';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

export interface UseStatefulRequestOptions {
    doLogError?(err: unknown): boolean;
    noCache?: boolean;
}

export interface UseStatefulRequestOutput<ResponseData = unknown, RequestData = unknown> {
    makeRequest: (config: AxiosRequestConfig<RequestData>) => Promise<AxiosResponse<ResponseData, RequestData>>;
    responseData?: ResponseData;
    loading: boolean;
    error: unknown;
    cancel: () => void;
    clear: () => void;
}

export const useStatefulRequest = <ResponseData = unknown, RequestData = unknown>(
    options?: UseStatefulRequestOptions,
): UseStatefulRequestOutput<ResponseData, RequestData> => {
    const [responseData, setResponseData] = useState<ResponseData>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>();

    const abortController = useRef<AbortController>();
    const { axiosInstance } = useAxios();

    const makeRequest = async (
        config: AxiosRequestConfig<RequestData>,
    ): Promise<AxiosResponse<ResponseData, RequestData>> => {
        abortController.current?.abort();
        const newAbortController = new AbortController();
        abortController.current = newAbortController;
        const signal = newAbortController.signal;

        setLoading(true);
        setError(undefined);
        try {
            const response = await axiosInstance.request<ResponseData, AxiosResponse<ResponseData>, RequestData>({
                ...config,
                signal,
            });

            setLoading(false);

            if (!options?.noCache) setResponseData(response.data);
            return response;
        } catch (e: unknown) {
            if (!options?.doLogError || (options.doLogError && options.doLogError(e))) {
                if (axios.isCancel(e)) {
                    console.log(`Cancelled request: ${config.method} ${config.url}`);
                    // Don't stop loading if cancelled
                    throw e;
                } else if (e instanceof Error) {
                    console.error('Request failed:', e.message, e.stack);
                } else {
                    console.error('Request failed:', e);
                }
            }

            setError(e);
            setLoading(false);
            throw e;
        }
    };

    const cancel = useCallback(() => {
        abortController.current?.abort();
        setLoading(false);
    }, []);

    const clear = useCallback(() => {
        setResponseData(undefined);
    }, []);

    // Abort request when hook is destroyed
    useEffect(() => {
        return cancel;
    }, []);

    return { makeRequest, responseData, loading, error, cancel, clear };
};
